import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  IKiAnswer,
  IKiAttachment,
  IKiCompareResult,
  IKiConfig,
  IKiAntwortEntwurf,
  IKiAusweiseResult,
  IKiGesellschafterliste,
  IKiGesellschafterlisteResult,
  IKiKontrollSchritt,
  IKiKontrollSpeichernResult,
  IKiGrundbuchAnalysis,
  IKiMandantResult,
  IKiOwnerResolution,
  IKiMessage,
  IKiPosteingangAnalysis,
  IKiRegisterabrufDatei,
  IKiRegisterabrufOhneMandant,
  IKiStatus,
} from '../../Interfaces/IKi';
import { customFetcher } from '../useFetch';

const KI_CONFIG_KEY = ['ki', 'config'];

/** Anbieter, Modelle und Hauptschalter — nur für Administratoren. */
export const useGetKiConfig = () => {
  const {
    data: kiConfig,
    isLoading: isLoadingKiConfig,
    refetch: refetchKiConfig,
  } = useQuery<IKiConfig>({
    queryKey: KI_CONFIG_KEY,
    queryFn: () => customFetcher({ endpoint: '/ki/config' }),
  });

  return { kiConfig, isLoadingKiConfig, refetchKiConfig };
};

export const useSaveKiConfig = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: saveKiConfig, isPending: isPendingSaveKiConfig } =
    useMutation({
      mutationFn: (uploadObject: Record<string, unknown>) =>
        customFetcher({
          endpoint: '/ki/config',
          body: uploadObject,
          method: 'PUT',
        }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: KI_CONFIG_KEY });
        // Der Status steuert das Chat-Fenster und hängt an denselben Werten.
        queryClient.invalidateQueries({ queryKey: ['ki', 'status'] });
      },
    });

  return { saveKiConfig, isPendingSaveKiConfig };
};

/** Ist der Chat benutzbar? Für jeden angemeldeten Benutzer. */
export const useGetKiStatus = () => {
  const { data: kiStatus, isLoading: isLoadingKiStatus } = useQuery<IKiStatus>({
    queryKey: ['ki', 'status'],
    queryFn: () => customFetcher({ endpoint: '/ki/status' }),
  });

  return { kiStatus, isLoadingKiStatus };
};

export const useAskKi = () => {
  const { mutateAsync: askKi, isPending: isPendingAskKi } = useMutation({
    mutationFn: (uploadObject: {
      Message: string;
      History?: IKiMessage[];
      Provider?: string;
      /**
       * Eine Datei, die an genau diese Frage hängt (#1336). Base64 wie überall
       * in DiNo — kein Multipart. Sie wird nicht gespeichert: sie geht mit der
       * Frage zum Anbieter und ist danach weg.
       */
      Attachment?: IKiAttachment;
      /**
       * Zufallswert, der nur zu dieser Anfrage gehört. Bricht der Benutzer ab,
       * entwertet DiNo darüber das Werkzeug-Token — das Token selbst bekommt
       * das Frontend nie zu sehen, es gehört nicht hierher.
       */
      AbbruchKennung?: string;
      /** Der Vorgang, auf dessen Seite der Chat offen ist (#1336). */
      idLegalTransaction?: number | null;
      /**
       * Abbruch durch den Benutzer (#1336). Beendet das **Warten**; die
       * Anfrage beim Anbieter läuft im Hintergrund zu Ende und gibt dabei
       * auch ihr Werkzeug-Token wieder frei. Ein echter Abbruch bis zum
       * Anbieter hinunter ginge nur mit einer eigenen Abbruchkennung je
       * Anfrage — für „ich habe mich vertippt" ist das der falsche Aufwand.
       */
      signal?: AbortSignal;
    }): Promise<IKiAnswer> => {
      const { signal, ...body } = uploadObject;

      return customFetcher({
        endpoint: '/ki/chat',
        body,
        method: 'POST',
        signal,
      });
    },
  });

  return { askKi, isPendingAskKi };
};

/** Dieselbe Frage an mehrere Anbieter — für den Modellvergleich. */
export const useCompareKi = () => {
  const { mutateAsync: compareKi, isPending: isPendingCompareKi } = useMutation(
    {
      mutationFn: (uploadObject: {
        Message: string;
        ProviderArray: string[];
      }): Promise<IKiCompareResult> =>
        customFetcher({
          endpoint: '/ki/compare',
          body: uploadObject,
          method: 'POST',
        }),
    }
  );

  return { compareKi, isPendingCompareKi };
};

/**
 * Ein Dokument des Posteingangs untersuchen lassen.
 *
 * Bewusst eine Mutation und keine Abfrage: das Ergebnis soll nicht im
 * Zwischenspeicher liegen und beim Blättern durch den Posteingang ungefragt
 * neu geholt werden — jede Untersuchung kostet einen Modellaufruf.
 */
export const useAnalysePosteingang = () => {
  const {
    mutateAsync: analysePosteingang,
    isPending: isPendingAnalysePosteingang,
  } = useMutation({
    mutationFn: (uploadObject: {
      idFSDirectory: number;
      path: string;
      fileName?: string;
    }): Promise<IKiPosteingangAnalysis> =>
      customFetcher({
        endpoint: '/ki/posteingang/analyse',
        body: uploadObject,
        method: 'POST',
      }),
  });

  return { analysePosteingang, isPendingAnalysePosteingang };
};

/**
 * Einen hochgeladenen Grundbuchauszug auslesen (#1336).
 *
 * Wie oben eine Mutation und keine Abfrage: jede Auslesung kostet einen
 * Modellaufruf und schickt das Dokument an den Anbieter — das darf nicht
 * beiläufig aus einem Zwischenspeicher heraus erneut passieren.
 */
export const useAnalyseGrundbuch = () => {
  const {
    mutateAsync: analyseGrundbuch,
    isPending: isPendingAnalyseGrundbuch,
  } = useMutation({
    mutationFn: (uploadObject: {
      Attachment: IKiAttachment;
    }): Promise<IKiGrundbuchAnalysis> =>
      customFetcher({
        endpoint: '/ki/grundbuch/analyse',
        body: uploadObject,
        method: 'POST',
      }),
  });

  return { analyseGrundbuch, isPendingAnalyseGrundbuch };
};

/**
 * Die Entscheidungen zu den ausgelesenen Eigentümern umsetzen (#1336).
 *
 * Legt dabei die als `neu` markierten Mandanten an und liefert das fertige
 * `OwnerArray` für das Blatt. Am Grundbuchblatt selbst wird noch nichts
 * geschrieben — das tut erst der Editor.
 */
/**
 * Antwortentwurf auf eine eingegangene E-Mail (#1336).
 *
 * Gibt nur Text zurück. Versendet wird über den gewohnten Weg, nachdem ein
 * Mensch gelesen und geändert hat — hier wird nichts gespeichert und nichts
 * angestoßen.
 */
export const useKiAntwortEntwurf = () => {
  const { mutateAsync: entwerfeAntwort, isPending: isPendingEntwerfeAntwort } =
    useMutation({
      mutationFn: (uploadObject: {
        idIncomingEmail: number;
        Hinweis?: string;
      }): Promise<IKiAntwortEntwurf> =>
        customFetcher({
          endpoint: '/ki/email/entwurf',
          body: uploadObject,
          method: 'POST',
        }),
    });

  return { entwerfeAntwort, isPendingEntwerfeAntwort };
};

/**
 * Eine abgebrochene Chat-Anfrage entwerten (#1336).
 *
 * Der Abbruch beendet nur das Warten; der Lauf beim Anbieter läuft weiter und
 * lässt sich bei watsonx Orchestrate nicht einmal beenden. Was bleibt, ist ihm
 * den Schlüssel zu nehmen: danach laufen seine Werkzeugaufrufe ins Leere statt
 * in echte Mandantendaten.
 */
export const useKiAbbrechen = () => {
  const { mutateAsync: meldeAbbruch } = useMutation({
    mutationFn: (uploadObject: {
      AbbruchKennung: string;
    }): Promise<{ Entwertet: number }> =>
      customFetcher({
        endpoint: '/ki/chat/abbrechen',
        body: uploadObject,
        method: 'POST',
      }),
  });

  return { meldeAbbruch };
};

/** Bestätigte Ausweis-Zuordnungen am Mandanten ablegen (#1336). */
export const useUebernehmeAusweise = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: uebernehmeAusweise,
    isPending: isPendingUebernehmeAusweise,
  } = useMutation({
    mutationFn: (uploadObject: {
      ZuordnungArray: Record<string, unknown>[];
    }): Promise<IKiAusweiseResult> =>
      customFetcher({
        endpoint: '/ki/ausweise/uebernehmen',
        body: uploadObject,
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['legalclient'] });
    },
  });

  return { uebernehmeAusweise, isPendingUebernehmeAusweise };
};

/** Die Entscheidungen zu den ausgelesenen Beteiligten umsetzen (#1336). */
export const useUebernehmeMandanten = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: uebernehmeMandanten,
    isPending: isPendingUebernehmeMandanten,
  } = useMutation({
    mutationFn: (uploadObject: {
      BeteiligteArray: Record<string, unknown>[];
    }): Promise<IKiMandantResult> =>
      customFetcher({
        endpoint: '/ki/mandant/uebernehmen',
        body: uploadObject,
        method: 'POST',
      }),
    onSuccess: () => {
      // Neu angelegte Mandanten sollen in der Mandantenliste auftauchen.
      void queryClient.invalidateQueries({ queryKey: ['legalclient'] });
    },
  });

  return { uebernehmeMandanten, isPendingUebernehmeMandanten };
};

/**
 * Registerdokument holen, ohne es abzulegen — für eine Gesellschaft, die
 * kein Mandant ist (#1336). Die Datei kommt zurück, gespeichert wird nichts.
 */
export const useKiRegisterabruf = () => {
  const { mutateAsync: registerabruf, isPending: isPendingRegisterabruf } =
    useMutation({
      mutationFn: (
        uploadObject: IKiRegisterabrufOhneMandant
      ): Promise<IKiRegisterabrufDatei> =>
        customFetcher({
          endpoint: '/ki/registerabruf',
          body: uploadObject,
          method: 'POST',
        }),
    });

  return { registerabruf, isPendingRegisterabruf };
};

/** Gesellschafterliste lesen lassen (#1336) — nur ein Vorschlag. */
export const useGesellschafterlisteAuslesen = () => {
  const { mutateAsync: liesGesellschafterliste, isPending: isPendingLies } =
    useMutation({
      mutationFn: (uploadObject: {
        Datei: { FileName: string; MimeType: string; Data: string };
        idLegalClient?: number | null;
      }): Promise<IKiGesellschafterliste> =>
        customFetcher({
          endpoint: '/ki/gesellschafterliste/auslesen',
          body: uploadObject,
          method: 'POST',
        }),
    });

  return { liesGesellschafterliste, isPendingLies };
};

/**
 * Eigentümer- und Kontrollstruktur: die Gesellschafter EINER Gesellschaft
 * aus dem Register. Die Kette nach oben steuert die Komponente.
 */
export const useKontrollstrukturSchritt = () => {
  const { mutateAsync: pruefeGesellschaft } = useMutation({
    mutationFn: (uploadObject: {
      Firma: string;
      Rechtsform?: string | null;
      Registergericht?: string | null;
      Registernummer?: string | null;
      /** Nur die Geschäftsleitung — für den fiktiven wirtschaftlich Berechtigten. */
      NurGeschaeftsleitung?: boolean;
    }): Promise<IKiKontrollSchritt> =>
      customFetcher({
        endpoint: '/ki/kontrollstruktur/schritt',
        body: uploadObject,
        method: 'POST',
      }),
  });

  return { pruefeGesellschaft };
};

/** Die Kontrollstruktur als PDF im GwG-Ordner eines Vorgangs ablegen. */
export const useKontrollstrukturSpeichern = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: speichereKontrollstruktur,
    isPending: isPendingSpeichern,
  } = useMutation({
    mutationFn: (uploadObject: {
      idLegalTransaction: number;
      idLegalClient?: number | null;
      Bericht: Record<string, unknown>;
      Bild?: string | null;
    }): Promise<IKiKontrollSpeichernResult> =>
      customFetcher({
        endpoint: '/ki/kontrollstruktur/speichern',
        body: uploadObject,
        method: 'POST',
      }),
    onSuccess: (_, uploadObject) => {
      void queryClient.invalidateQueries({
        queryKey: [
          'legaltransaction/fullObject',
          uploadObject.idLegalTransaction,
        ],
      });
    },
  });

  return { speichereKontrollstruktur, isPendingSpeichern };
};

/** Gesellschaft und Gesellschafter anlegen, Beziehung „Firma" ergänzen. */
export const useGesellschafterlisteUebernehmen = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: uebernehmeGesellschafter,
    isPending: isPendingUebernehmeGesellschafter,
  } = useMutation({
    mutationFn: (uploadObject: {
      Gesellschaft: Record<string, unknown>;
      GesellschafterArray: Record<string, unknown>[];
    }): Promise<IKiGesellschafterlisteResult> =>
      customFetcher({
        endpoint: '/ki/gesellschafterliste/uebernehmen',
        body: uploadObject,
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['legalclient'] });
      void queryClient.invalidateQueries({
        queryKey: ['legalclient/fullObject'],
      });
    },
  });

  return { uebernehmeGesellschafter, isPendingUebernehmeGesellschafter };
};

export const useResolveGrundbuchOwners = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: resolveGrundbuchOwners,
    isPending: isPendingResolveGrundbuchOwners,
  } = useMutation({
    mutationFn: (uploadObject: {
      OwnerArray: Record<string, unknown>[];
    }): Promise<IKiOwnerResolution> =>
      customFetcher({
        endpoint: '/ki/grundbuch/owners/resolve',
        body: uploadObject,
        method: 'POST',
      }),
    onSuccess: () => {
      // Neu angelegte Mandanten sollen in der Mandantenliste auftauchen.
      void queryClient.invalidateQueries({ queryKey: ['legalclient'] });
    },
  });

  return { resolveGrundbuchOwners, isPendingResolveGrundbuchOwners };
};
